import { useState, useEffect } from 'react'
import { ChevronDown, Download, Video } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import { downloadRemoteFile } from '@/lib/download'

export interface VideoSuggestion {
  id: string
  videoUrl: string
  filename: string
  fileSize: number
  contentType: string
  caption?: string
  deliverableId?: string
  sameAsDeliverableId?: string
  createdAt: string
}

interface VideoSuggestionSheetProps {
  suggestion: VideoSuggestion
  onAccept: (caption?: string) => void
  onClose: () => void
}

const VideoSuggestionSheet = ({ suggestion, onAccept, onClose }: VideoSuggestionSheetProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true))
    document.body.style.overflow = 'hidden'
    return () => {
      cancelAnimationFrame(frame)
      document.body.style.overflow = ''
    }
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 280)
  }

  const handleAccept = () => {
    onAccept(suggestion.caption)
    handleClose()
  }

  const handleDownload = async () => {
    try {
      setIsDownloading(true)

      await downloadRemoteFile({
        url: suggestion.videoUrl,
        fileName: suggestion.filename || 'video-suggestion.mp4',
        contentType: suggestion.contentType,
      })

      toast('Video Downloaded', {
        description: 'Your video has been downloaded to your device.',
      })
    } catch (error: any) {
      toast.error('Download Failed', {
        description:
          error?.message || 'We could not download the suggested video. Please try again.',
      })
    } finally {
      setIsDownloading(false)
    }
  }

  const fileSizeMB = suggestion.fileSize ? (suggestion.fileSize / (1024 * 1024)).toFixed(1) : null

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end sm:items-center sm:justify-center">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      {/* Sheet — bottom sheet on mobile, centered tall modal on desktop */}
      <div
        className={`relative flex flex-col bg-[#0f1623] rounded-t-3xl sm:rounded-3xl transition-transform sm:transition-[opacity,transform] duration-300 ease-out w-full sm:w-auto sm:max-w-sm ${
          isVisible
            ? 'translate-y-0 sm:scale-100 sm:opacity-100'
            : 'translate-y-full sm:scale-95 sm:opacity-0'
        }`}
        style={{ height: '95dvh', maxHeight: '95dvh' }}
      >
        {/* Drag handle (mobile only) */}
        <div className="flex justify-center pt-3 pb-1 shrink-0 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-white/10 border border-white/15 flex items-center justify-center">
              <Video className="w-3.5 h-3.5 text-gray-300" />
            </div>
            <div>
              <p className="text-white text-sm font-semibold leading-tight">Video Suggestion</p>
              <p className="text-gray-500 text-xs">
                Uploaded by your manager &middot;{' '}
                {new Date(suggestion.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/10"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>

        {/* Video — fills all available height between header and footer */}
        <div className="flex-1 min-h-0 bg-black">
          <video
            controls
            playsInline
            className="w-full h-full object-contain"
            src={suggestion.videoUrl}
          >
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Info + CTAs — fixed at bottom, compact */}
        <div className="shrink-0 bg-[#0f1623] border-t border-white/8">
          {/* File row */}
          <div className="flex items-center gap-2.5 px-4 pt-3 pb-2">
            <Video className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-white text-xs truncate">{suggestion.filename}</p>
              {fileSizeMB && <p className="text-gray-500 text-[11px]">{fileSizeMB} MB</p>}
            </div>
            <button
              type="button"
              onClick={() => void handleDownload()}
              disabled={isDownloading}
              className="flex items-center gap-1 text-xs text-[#C0FD72] hover:text-[#b0ef62] transition-colors shrink-0 disabled:opacity-60"
            >
              <Download className="w-3.5 h-3.5" />
              {isDownloading ? 'Preparing...' : 'Download'}
            </button>
          </div>

          {/* Suggested caption (collapsed to 2 lines) */}
          {suggestion.caption && (
            <div className="mx-4 mb-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
              <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">
                Suggested caption
              </p>
              <p className="text-white text-xs leading-relaxed line-clamp-2">
                {suggestion.caption}
              </p>
            </div>
          )}

          {/* CTAs */}
          <div className="px-4 pb-8 pt-2 space-y-2">
            <button
              onClick={handleAccept}
              className="w-full py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors bg-[#C0FD72] text-black hover:bg-[#b0ef62]"
            >
              Accept &amp; Edit Caption →
            </button>
            <button
              onClick={handleClose}
              className="w-full py-2.5 text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              Upload my own instead
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VideoSuggestionSheet
